/**
 * HOF-219 — how the document-edge component reaches its unlink.
 *
 * ``edgeTypes`` is a module-level constant (it must be referentially stable, or
 * ReactFlow remounts every edge on each render), so ``DocumentRefEdge`` cannot
 * be given props by ``MatchGraph``. A context is the idiomatic way in:
 * ``MatchGraph`` provides it around ``<ReactFlow>``, and provides ``null`` when
 * ``onDocumentUnlink`` is absent — which is what makes a Storybook or
 * standalone render show no control at all (AC 14).
 *
 * A SECOND context rather than a widened ``MatchedLineEdgeActions``: the two
 * carry different payload types (a line pair vs. a document pair) and are wired
 * from different props, so one union would force every consumer to narrow at
 * runtime what the type system already knows.
 */
import { createContext, useContext } from 'react';

import type { DocumentEdgeLike } from '../documentLinkValidation';
import type { LineUnlinkTrigger } from '../lineUnlinkTelemetry';

export interface DocumentRefEdgeActions {
  requestDocumentUnlink: (edge: DocumentEdgeLike, trigger: LineUnlinkTrigger) => boolean;
}

export const DocumentRefEdgeActionsContext = createContext<DocumentRefEdgeActions | null>(null);

export function useDocumentRefEdgeActions(): DocumentRefEdgeActions | null {
  return useContext(DocumentRefEdgeActionsContext);
}
